/**
 *  All api related to system setting
 */

import _ from 'lodash'
import { CancelToken } from 'axios'
import { OmnitechApi } from '../../api'
import { cancelRequest } from '../../helpers'
import { isErrorExist, convertResponseToErrorObject } from '../../omnitech-error'

export function fetchOrders(options = {}) {
  const { page = 1, ...others } = options
  const params = {
    page,
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchOrders',
    () => source.cancel('fetchOrders: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `orders`,
      method: 'get',
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets'
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      orders: data.orders,
      pagination: data.ordersPagination,
      next: _.get(data, 'ordersPagination.nextPage') > page
        ? () => fetchOrders({
          page: page + 1,
          ...others
        })
        : null,
      count: _.get(data, 'ordersPagination.totalCount')
    }))
}

export function fetchOrder(options = {}) {
  const { id, ...others } = options
  const params = {
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchOrder',
    () => source.cancel('fetchOrder: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `orders/${id}`,
      method: 'get',
      params,
      cancelToken: source.token
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      order: data.order
    }))
}

export function fetchOrderByReferenceNumber(options = {}) {
  const { referenceNumber, ...others } = options
  const params = {
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchOrderByReferenceNumber',
    () => source.cancel('fetchOrderByReferenceNumber: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `orders/by_reference_number/${referenceNumber}`,
      method: 'get',
      params,
      cancelToken: source.token
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      order: data.order
    }))
}

export function fetchOrderByUuid(options = {}) {
  const { uuid, ...others } = options
  const params = {
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchOrderByUuid',
    () => source.cancel('fetchOrderByUuid: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `orders/by_uuid/${uuid}`,
      method: 'get',
      params,
      cancelToken: source.token
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      order: data.order
    }))
}

export function updateOrder({
  referenceNumber,
  uuid,
  // byType, support by_reference_number and by_uuid
  // once all api is updated and support by uuid, can remove by_reference_number
  byType = 'by_reference_number',
  action,
  params = {},
  data = {},
  ...otherOptions
}) {
  const source = CancelToken.source()
  cancelRequest.add(
    'updateOrder',
    () => source.cancel('updateOrder: Operation cancelled by user')
  )
  let actionType = ''
  switch (action) {
    case 'cancel':
      actionType = '__cancel_order'
      break
    case 'decline':
      actionType = '__try_decline_order'
      break
    default:
      actionType = ''
      break
  }

  const endpoint = `orders/${byType}/${byType === 'by_reference_number' ? referenceNumber : uuid}`

  return OmnitechApi
    .request({
      endpoint,
      method: 'put',
      params: {
        ...params
      },
      data: {
        ...(
          _.isEmpty(actionType)
            ? {
              data: {
                order: data,
              }
            }
            : {
              data: {
                [actionType]: {},
              }
            }
        ),
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      order: data.order
    }))
}

export const updateOrderToDecline = (params) => updateOrder({
  action: 'decline',
  ...params
})
